import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import ModuleAccessDenied from '../components/ui/ModuleAccessDenied';
import {
  Clock, CalendarDays, Lock, CheckCircle, CircleDot, XCircle, CircleDashed,
  AlertCircle, Plane, X, Timer, Undo2,
} from 'lucide-react';
import { useOrgTimezone, formatLocalDateInTz, getCurrentPayPeriod } from '../utils/timezone';
import organizationService from '../services/organization';
import myTimesheetService, { type MyPeriodData, type MyPeriodDay } from '../services/myTimesheet';
import leaveService, {
  type CreateLeaveRequest, type LeaveBalance, type LeaveDayInfo, type LeaveDaySegment,
  type LeaveRequest, type LeaveType,
} from '../services/leave';

// UTC-midnight date shift, matching the pay-period arithmetic in timezone.ts.
function addDaysUTC(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

const WEEKDAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Postgres NUMERIC values can arrive as strings over the API — coerce before
// formatting so a string never trips `.toFixed`.
const hrs = (v: unknown): string => {
  const n = Number(v);
  return (Number.isFinite(n) ? n : 0).toFixed(1);
};
const asNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Leave types offered by the quick shortfall pills, with the entitlement key.
const LEAVE_TYPE_OPTIONS: { value: LeaveType; label: string }[] = [
  { value: 'personal', label: 'Personal / Sick' },
  { value: 'annual', label: 'Annual' },
  { value: 'time_in_lieu', label: 'Time in Lieu' },
  { value: 'long_service', label: 'Long Service' },
  { value: 'unpaid', label: 'Unpaid' },
];

const APPROVAL_CHIP: Record<string, { label: string; className: string; icon: typeof CheckCircle }> = {
  approved: { label: 'Approved', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700', icon: CircleDot },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700', icon: XCircle },
  unsubmitted: { label: 'Not submitted', className: 'bg-neutral-100 text-neutral-500', icon: CircleDashed },
};

/** A contiguous stretch of days that can safely take whole-day leave. */
type LeaveRun = { start: string; end: string; days: number; hours: number };

type LeaveModalState =
  | { kind: 'day'; day: MyPeriodDay }
  | { kind: 'range'; runs: LeaveRun[] };

/**
 * How much of a date is already spoken for by leave.
 * `fullDay` means a whole-day or multi-day request covers it, so nothing more
 * can be added. Otherwise `hours` is what partial-day requests have claimed so
 * far, and the rest of the day's shortfall is still claimable.
 */
type LeaveCoverage = { hours: number; fullDay: boolean; request: LeaveRequest };

function MyTimesheet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();

  const payFrequency: 'weekly' | 'fortnightly' = user?.pay_frequency === 'weekly' ? 'weekly' : 'fortnightly';

  const [orgSettings, setOrgSettings] = useState<{ payCycleStartDay: number; fortnightlyRefDate: string | null } | null>(null);
  const [tab, setTab] = useState<'current' | 'last'>('current');
  const [data, setData] = useState<MyPeriodData | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moduleBlocked, setModuleBlocked] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [leaveModal, setLeaveModal] = useState<LeaveModalState | null>(null);
  // A shortfall day has two honest explanations — the hours were worked and
  // never clocked, or they weren't worked at all — so the day's pill opens a
  // chooser instead of going straight to the leave form.
  const [fixDay, setFixDay] = useState<MyPeriodDay | null>(null);
  // Keyed by date: a day can hold more than one declaration row (one per
  // untouched stretch of the roster), and they are withdrawn together.
  const [withdrawingDate, setWithdrawingDate] = useState<string | null>(null);

  // Resolve the current + last pay periods from org settings and the user's frequency.
  const periods = useMemo(() => {
    if (!orgSettings) return null;
    const current = getCurrentPayPeriod(orgSettings.payCycleStartDay, payFrequency, orgTz, orgSettings.fortnightlyRefDate);
    const span = payFrequency === 'fortnightly' ? 14 : 7;
    const last = {
      startDate: addDaysUTC(current.startDate, -span),
      endDate: addDaysUTC(current.endDate, -span),
    };
    return { current, last };
  }, [orgSettings, payFrequency, orgTz]);

  const selectedPeriod = periods ? (tab === 'current' ? periods.current : periods.last) : null;

  // Load org settings + leave balances once.
  useEffect(() => {
    organizationService.getCurrentOrganization().then(res => {
      if (res.success && res.data) {
        const d = res.data as { pay_cycle_start_day?: number; fortnightly_cycle_reference_date?: string | null };
        setOrgSettings({
          payCycleStartDay: d.pay_cycle_start_day ?? 4,
          fortnightlyRefDate: d.fortnightly_cycle_reference_date ? String(d.fortnightly_cycle_reference_date).slice(0, 10) : null,
        });
      } else {
        setOrgSettings({ payCycleStartDay: 4, fortnightlyRefDate: null });
      }
    }).catch(() => setOrgSettings({ payCycleStartDay: 4, fortnightlyRefDate: null }));

    leaveService.getBalances().then(res => {
      if (res.success && res.data) setBalances(res.data);
    }).catch(() => { /* non-critical */ });
  }, []);

  const loadPeriod = useCallback(async () => {
    if (!selectedPeriod) return;
    setLoading(true);
    setError(null);
    try {
      const [periodRes, leaveRes] = await Promise.all([
        myTimesheetService.getMyPeriod(selectedPeriod.startDate, selectedPeriod.endDate),
        leaveService.getRequests(),
      ]);
      if (periodRes.success && periodRes.data) {
        setData(periodRes.data);
      } else {
        setError('Failed to load your timesheet');
      }
      if (leaveRes.success && leaveRes.data) setLeaveRequests(leaveRes.data);
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 403) setModuleBlocked(true);
      else setError((err as { message?: string })?.message || 'Failed to load your timesheet');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadPeriod();
  }, [selectedPeriod, loadPeriod]);

  // Leave already requested, tallied per date. Tracked in HOURS rather than as
  // a set of covered dates: a day that was short at both ends needs more than
  // one partial-day request, and marking the whole date as covered after the
  // first one would strand the remaining shortfall.
  const leaveByDate = useMemo(() => {
    const map = new Map<string, LeaveCoverage>();
    const bump = (date: string, request: LeaveRequest, hours: number, fullDay: boolean) => {
      const cur = map.get(date);
      if (!cur) map.set(date, { hours, fullDay, request });
      else {
        cur.hours += hours;
        cur.fullDay = cur.fullDay || fullDay;
      }
    };
    for (const lr of leaveRequests) {
      if (lr.status !== 'pending' && lr.status !== 'approved') continue;
      const start = lr.start_date.slice(0, 10);
      const end = lr.end_date.slice(0, 10);
      // Only PENDING hours are subtracted here. Approving leave writes it to
      // the timesheet as an entry, so approved hours are already inside the
      // day's `worked_hours` and have shrunk its shortfall — counting them
      // again would hide the part of the day still left to claim.
      const unbookedHours = lr.status === 'pending' ? asNum(lr.hours) : 0;
      // A partial-day request carries a time window and an hour count; anything
      // else takes the whole day.
      if (lr.start_time && lr.end_time && start === end) {
        bump(start, lr, unbookedHours, false);
        continue;
      }
      let cursor = start;
      // Guard against malformed ranges.
      let guard = 0;
      while (cursor <= end && guard < 400) {
        bump(cursor, lr, 0, true);
        cursor = addDaysUTC(cursor, 1);
        guard++;
      }
    }
    return map;
  }, [leaveRequests]);

  // Shortfall on a day after subtracting leave already requested for it.
  const remainingShortfall = useCallback((day: MyPeriodDay): number => {
    const cov = leaveByDate.get(day.date);
    if (cov?.fullDay) return 0;
    return Math.max(0, day.shortfall_hours - (cov?.hours ?? 0));
  }, [leaveByDate]);

  const availableFor = (type: LeaveType): number | null => {
    const b = balances.find(x => x.leave_type === type);
    return b ? asNum(b.available_hours) : null;
  };

  const handleLeaveSubmitted = (message: string) => {
    setLeaveModal(null);
    setSuccess(message);
    setTimeout(() => setSuccess(null), 4000);
    loadPeriod();
  };

  const handleHoursDeclared = (message: string) => {
    setFixDay(null);
    setSuccess(message);
    setTimeout(() => setSuccess(null), 6000);
    loadPeriod();
  };

  // A day short at both ends is declared as two rows, so withdrawing has to
  // take both. They are withdrawn in sequence and the first failure stops the
  // rest: a half-withdrawn day would leave the employee looking at a shortfall
  // with no way to tell which stretch was still claimed.
  const handleWithdrawDeclared = async (date: string, entryIds: number[]) => {
    if (entryIds.length === 0) return;
    const what = entryIds.length > 1
      ? `Withdraw all ${entryIds.length} stretches of scheduled hours you declared for this day?`
      : 'Withdraw the scheduled hours you declared for this day?';
    if (!window.confirm(what)) return;
    setWithdrawingDate(date);
    setError(null);
    try {
      for (const entryId of entryIds) {
        const res = await myTimesheetService.withdrawMissedClock(entryId);
        if (!res.success) {
          setError((res as { error?: { message?: string } }).error?.message || 'Failed to withdraw the declared hours');
          loadPeriod();
          return;
        }
      }
      setSuccess('Declared hours withdrawn.');
      setTimeout(() => setSuccess(null), 4000);
      loadPeriod();
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to withdraw the declared hours');
      loadPeriod();
    } finally {
      setWithdrawingDate(null);
    }
  };

  // Whole-day leave can only be inserted on days with NO work recorded — a
  // full scheduled entry on a day that already has one collides with the
  // timesheet overlap guard at approval time and rolls back the entire
  // request. So the "request a range" action is split into contiguous runs of
  // clean days; a day that was partly worked is claimed on its own, from its
  // row, as partial-day leave.
  //
  // Days that are neither claimable nor blocking (weekends, public holidays)
  // don't break a run — approval skips them — but they can't end one either.
  const rangeRuns = useMemo<LeaveRun[]>(() => {
    const runs: LeaveRun[] = [];
    let cur: LeaveRun | null = null;
    for (const d of data?.days ?? []) {
      const cov = leaveByDate.get(d.date);
      const blocks = d.worked_hours > 0 || d.locked || !!cov;
      if (blocks) {
        if (cur) { runs.push(cur); cur = null; }
        continue;
      }
      if (remainingShortfall(d) > 0.01 && !d.is_public_holiday) {
        if (cur) {
          cur.end = d.date;
          cur.days += 1;
          cur.hours += d.shortfall_hours;
        } else {
          cur = { start: d.date, end: d.date, days: 1, hours: d.shortfall_hours };
        }
      }
    }
    if (cur) runs.push(cur);
    return runs;
  }, [data, leaveByDate, remainingShortfall]);

  if (moduleBlocked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="My Timesheet" subtitle="Your pay-cycle timesheet" icon={<Clock className="h-6 w-6 text-white" />} showHistoryButton={false} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ModuleAccessDenied moduleName="Timesheet" />
        </div>
      </div>
    );
  }

  // Rows worth showing: working days, or any day with entries / leave / a public holiday.
  const visibleDays = (data?.days ?? []).filter(d =>
    d.is_working_day || d.entries.length > 0 || d.is_public_holiday || d.is_leave
  );

  const shortfallDays = (data?.days ?? []).filter(d => remainingShortfall(d) > 0.01 && !d.locked);

  const rangeDayCount = rangeRuns.reduce((n, r) => n + r.days, 0);
  const rangeHours = rangeRuns.reduce((n, r) => n + r.hours, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="My Timesheet"
        subtitle="Review your current and last pay cycle, and account for any shortfall"
        icon={<Clock className="h-6 w-6 text-white" />}
        showHistoryButton
        onHistoryClick={() => navigate('/timesheet/history')}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex rounded-lg border border-neutral-200 overflow-hidden w-fit mb-6">
          {(['current', 'last'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                tab === t ? 'bg-primary-600 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              {t === 'current' ? 'Current pay cycle' : 'Last pay cycle'}
            </button>
          ))}
        </div>

        {selectedPeriod && (
          <p className="text-sm text-neutral-500 mb-4 flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            {formatLocalDateInTz(selectedPeriod.startDate, orgTz)} — {formatLocalDateInTz(selectedPeriod.endDate, orgTz)}
            <span className="text-neutral-300">·</span>
            <span className="capitalize">{payFrequency}</span>
          </p>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 shrink-0" /> {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        {data?.locked && (
          <div className="mb-4 p-3 bg-neutral-800 text-white rounded-lg text-sm flex items-center gap-2">
            <Lock className="h-4 w-4 shrink-0" />
            This pay cycle has been finalised for payroll. It can no longer be changed — contact HR if something looks wrong.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : data ? (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <SummaryTile label="Scheduled" value={data.totals.scheduled_hours} className="text-neutral-800" />
              <SummaryTile label="Worked / covered" value={data.totals.worked_hours} className="text-primary-700" />
              <SummaryTile label="Shortfall" value={data.totals.shortfall_hours} className={data.totals.shortfall_hours > 0 ? 'text-amber-700' : 'text-neutral-400'} />
            </div>

            {/* Whole-period shortfall prompt */}
            {shortfallDays.length > 0 && !data.locked && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-col sm:flex-row sm:items-center gap-3">
                <Plane className="h-5 w-5 text-amber-600 shrink-0" />
                <div className="flex-1 text-sm text-amber-900">
                  <p className="font-semibold">
                    You have {hrs(shortfallDays.reduce((n, d) => n + remainingShortfall(d), 0))}h of shortfall across{' '}
                    {shortfallDays.length} day{shortfallDays.length !== 1 ? 's' : ''}.
                  </p>
                  <p>Worked a day but forgot to clock in? Claim it as normal hours from its own row below.</p>
                  {rangeRuns.length > 0 ? (
                    <p>
                      Off sick or on leave? Request whole-day leave for the {rangeDayCount} day
                      {rangeDayCount !== 1 ? 's' : ''} you didn't work at all ({hrs(rangeHours)}h).
                      {shortfallDays.length > rangeDayCount && ' Days you worked part of are requested from their own row below.'}
                    </p>
                  ) : (
                    <p>Every short day here has some work recorded — request those from their own row below, so the leave fits around the hours you worked.</p>
                  )}
                </div>
                {rangeRuns.length > 0 && (
                  <Button
                    onClick={() => setLeaveModal({ kind: 'range', runs: rangeRuns })}
                    className="shrink-0"
                  >
                    Request leave for {rangeDayCount} day{rangeDayCount !== 1 ? 's' : ''}
                  </Button>
                )}
              </div>
            )}

            {/* Day list */}
            <Card>
              <CardContent className="p-0">
                {visibleDays.length === 0 ? (
                  <div className="py-12 text-center text-neutral-500">No scheduled days or entries in this pay cycle.</div>
                ) : (
                  <div className="divide-y divide-neutral-100">
                    {visibleDays.map(day => (
                      <DayRow
                        key={day.date}
                        day={day}
                        orgTz={orgTz}
                        coverage={leaveByDate.get(day.date)}
                        remaining={remainingShortfall(day)}
                        onFix={() => setFixDay(day)}
                        onWithdrawDeclared={handleWithdrawDeclared}
                        withdrawingDate={withdrawingDate}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {fixDay && (
        <FixDayModal
          day={fixDay}
          orgTz={orgTz}
          remaining={remainingShortfall(fixDay)}
          onClose={() => setFixDay(null)}
          onRequestLeave={() => {
            const day = fixDay;
            setFixDay(null);
            setLeaveModal({ kind: 'day', day });
          }}
          onDeclared={handleHoursDeclared}
        />
      )}

      {leaveModal && (
        <LeaveRequestModal
          state={leaveModal}
          orgTz={orgTz}
          availableFor={availableFor}
          onClose={() => setLeaveModal(null)}
          onSubmitted={handleLeaveSubmitted}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, className }: { label: string; value: number; className: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-xs text-neutral-500 font-medium mb-1">{label}</p>
        <p className={`text-lg font-bold ${className}`}>{hrs(value)}h</p>
      </CardContent>
    </Card>
  );
}

function DayRow({ day, orgTz, coverage, remaining, onFix, onWithdrawDeclared, withdrawingDate }: {
  day: MyPeriodDay;
  orgTz: string;
  coverage: LeaveCoverage | undefined;
  /** Shortfall left after leave already requested for this date. */
  remaining: number;
  onFix: () => void;
  onWithdrawDeclared: (date: string, entryIds: number[]) => void;
  withdrawingDate: string | null;
}) {
  const chip = APPROVAL_CHIP[day.approval_status] || APPROVAL_CHIP.unsubmitted;
  const ChipIcon = chip.icon;
  // Hours the employee declared off their roster because no clock record
  // exists. A day short before the first clock-in AND after the last one is
  // declared as one row per stretch, so this is a list.
  const declared = day.entries.filter(e => e.missed_clock);
  const declaredHours = declared.reduce((n, e) => n + asNum(e.total_hours), 0);
  // Withdrawable only while it's still the employee's to take back — once the
  // period is submitted or locked, it's the approver's call.
  const withdrawable = declared.length > 0 && !day.locked
    && (day.approval_status === 'unsubmitted' || day.approval_status === 'rejected')
    ? declared
    : null;
  const withdrawing = withdrawingDate === day.date;
  // Still offer the pill when leave only covers part of the day — a day that
  // was short at both ends takes more than one request to cover.
  const showPill = remaining > 0.01 && !day.locked && !day.is_public_holiday;

  return (
    <div className="flex items-center justify-between px-4 py-3 gap-3">
      <div className="min-w-0">
        <p className="font-medium text-neutral-900">
          {WEEKDAY_NAMES[day.weekday]}, {formatLocalDateInTz(day.date, orgTz)}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {day.is_public_holiday && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" title={day.holiday_name || ''}>
              {day.holiday_name || 'Public Holiday'}
            </span>
          )}
          {coverage && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              <Plane className="h-3 w-3" />
              {coverage.request.status === 'approved' ? 'On leave' : 'Leave requested'}
              {!coverage.fullDay && coverage.hours > 0 && ` · ${hrs(coverage.hours)}h`}
            </span>
          )}
          {declared.length > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700"
              title={declared[0].missed_clock_reason
                ? `No clock record — ${declared[0].missed_clock_reason}`
                : 'Scheduled hours claimed — no clock in/out recorded'}
            >
              <Timer className="h-3 w-3" /> {hrs(declaredHours)}h claimed, no clock record
            </span>
          )}
          {day.entries.length > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
              <ChipIcon className="h-3 w-3" /> {chip.label}
            </span>
          )}
          {day.locked && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-800 text-white">
              <Lock className="h-3 w-3" /> Locked
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <p className="text-sm font-semibold text-neutral-900">{hrs(day.worked_hours)}h</p>
          <p className="text-xs text-neutral-400">of {hrs(day.scheduled_hours)}h</p>
        </div>
        {withdrawable && (
          <button
            onClick={() => onWithdrawDeclared(day.date, withdrawable.map(e => e.id))}
            disabled={withdrawing}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-neutral-500 hover:bg-neutral-100 disabled:opacity-50 transition-colors"
            title="Withdraw the scheduled hours you claimed for this day"
          >
            <Undo2 className="h-3 w-3" /> {withdrawing ? 'Withdrawing…' : 'Withdraw'}
          </button>
        )}
        {showPill ? (
          <button
            onClick={onFix}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
            title="Claim these hours as worked, or request leave to cover them"
          >
            −{hrs(remaining)}h · Account for {coverage ? 'the rest' : 'this'}
          </button>
        ) : (
          remaining > 0.01 && !day.is_public_holiday && (
            <span className="text-xs text-neutral-400">−{hrs(remaining)}h</span>
          )
        )}
      </div>
    </div>
  );
}

// A shortfall day is either work that was never clocked, or genuine absence.
// Leave used to be the only self-service answer, which meant employees burnt
// entitlement for days they had actually worked. This asks which it was, then
// routes to the matching action: normal hours straight off their roster, or the
// leave form as before.
function FixDayModal({ day, orgTz, remaining, onClose, onRequestLeave, onDeclared }: {
  day: MyPeriodDay;
  orgTz: string;
  /** Shortfall left after any leave already requested for this date. */
  remaining: number;
  onClose: () => void;
  onRequestLeave: () => void;
  onDeclared: (message: string) => void;
}) {
  const [mode, setMode] = useState<'choose' | 'worked'>('choose');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateLabel = formatLocalDateInTz(day.date, orgTz);
  const scheduledWindow = day.scheduled_start_time && day.scheduled_end_time
    ? `${day.scheduled_start_time}–${day.scheduled_end_time}`
    : null;
  const partlyWorked = day.worked_hours > 0;

  // Declared hours are written into the untouched stretches of the rostered
  // day, so a day whose entries already span its start and finish has nothing
  // to write even while it is short. Offering "I worked" there produced a
  // refusal the employee could do nothing about, so the option states the
  // truth instead. Clamped to `remaining` so the modal never promises more
  // than the day's own pill does.
  const claimable = Math.min(day.claimable_hours ?? remaining, remaining);
  const canClaim = claimable > 0.01;
  // Some of the shortfall is reachable and some is not — say so rather than
  // quietly claiming less than the pill advertised.
  const partiallyClaimable = canClaim && claimable < remaining - 0.01;

  const submitWorked = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await myTimesheetService.declareMissedClock(day.date, reason.trim() || undefined);
      if (res.success && res.data) {
        onDeclared(
          `${hrs(res.data.claimed_hours)}h of normal hours recorded for ${dateLabel}. ` +
          'Your supervisor still has to approve it.'
        );
      } else {
        setError((res as { error?: { message?: string } }).error?.message || 'Failed to record your scheduled hours');
      }
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to record your scheduled hours');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            {mode === 'choose' ? 'Account for this day' : 'Claim your scheduled hours'}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-neutral-600">
            <p><span className="font-medium text-neutral-800">{dateLabel}</span></p>
            <p className="mt-1">
              {hrs(remaining)}h short of your {hrs(day.scheduled_hours)}h scheduled
              {scheduledWindow ? ` (${scheduledWindow})` : ''}
              {partlyWorked ? ` · ${hrs(day.worked_hours)}h already recorded` : ''}.
            </p>
          </div>

          {mode === 'choose' ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setMode('worked')}
                disabled={!canClaim}
                className="w-full text-left p-3 rounded-lg border border-neutral-300 enabled:hover:border-primary-500 enabled:hover:bg-primary-50 transition-colors flex items-start gap-3 disabled:bg-neutral-50 disabled:cursor-not-allowed"
              >
                <Clock className={`h-5 w-5 shrink-0 mt-0.5 ${canClaim ? 'text-primary-600' : 'text-neutral-400'}`} />
                <span>
                  <span className={`block text-sm font-medium ${canClaim ? 'text-neutral-900' : 'text-neutral-500'}`}>
                    I worked — I forgot to clock in
                  </span>
                  <span className="block text-xs text-neutral-500 mt-0.5">
                    {canClaim
                      ? `Claim ${hrs(claimable)}h of normal hours from your work schedule. No leave is used.`
                      : 'Your entries already run from your scheduled start to your scheduled finish, so there are no unclocked hours to claim. Edit one of those entries, or ask your supervisor to correct the day.'}
                  </span>
                  {partiallyClaimable && (
                    <span className="block text-xs text-amber-700 mt-1">
                      Only {hrs(claimable)}h of the {hrs(remaining)}h falls outside the hours you already
                      have recorded. The rest needs an edit to an existing entry.
                    </span>
                  )}
                </span>
              </button>

              <button
                type="button"
                onClick={onRequestLeave}
                className="w-full text-left p-3 rounded-lg border border-neutral-300 hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-start gap-3"
              >
                <Plane className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <span>
                  <span className="block text-sm font-medium text-neutral-900">I wasn't at work — request leave</span>
                  <span className="block text-xs text-neutral-500 mt-0.5">
                    Cover the shortfall with personal, annual, time in lieu or unpaid leave.
                  </span>
                </span>
              </button>
            </div>
          ) : (
            <>
              <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200 text-sm text-neutral-700">
                <p className="font-medium text-neutral-900 mb-1">{hrs(claimable)}h as normal hours</p>
                <p className="text-xs">
                  {partlyWorked
                    ? 'Recorded against every untouched stretch of your scheduled day, around the hours you did clock — one entry per stretch.'
                    : `Recorded against your scheduled ${scheduledWindow || 'hours'}, less your unpaid break.`}
                </p>
                <p className="text-xs mt-1">
                  It goes to your supervisor for approval like any other entry, and is flagged as
                  having no clock in or clock out behind it. Once approved it counts as worked
                  hours — so it is paid as normal time and accrues leave the same way.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Why was there no clock record? (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="e.g. Phone battery flat — started in the back block"
                />
              </div>
            </>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={mode === 'worked' ? () => setMode('choose') : onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
            >
              {mode === 'worked' ? 'Back' : 'Cancel'}
            </button>
            {mode === 'worked' && (
              <Button onClick={submitWorked} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit for approval'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LeaveRequestModal({ state, orgTz, availableFor, onClose, onSubmitted }: {
  state: LeaveModalState;
  orgTz: string;
  availableFor: (t: LeaveType) => number | null;
  onClose: () => void;
  onSubmitted: (message: string) => void;
}) {
  const [leaveType, setLeaveType] = useState<LeaveType>('personal');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A day that was partly worked is claimed as one or more explicit windows.
  // The server works out which parts of the scheduled day are still uncovered
  // — a short day can be short before the first clock-in, after the last
  // clock-out, or both — so the form is driven off that list rather than off a
  // single clock time.
  const isPartialDay = state.kind === 'day' && state.day.worked_hours > 0;
  const [segments, setSegments] = useState<LeaveDaySegment[]>([]);
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [dayInfo, setDayInfo] = useState<LeaveDayInfo | null>(null);
  const [prepping, setPrepping] = useState(isPartialDay);
  const [prepError, setPrepError] = useState<string | null>(null);

  useEffect(() => {
    if (state.kind !== 'day' || state.day.worked_hours === 0) return;
    setPrepping(true);
    setPrepError(null);
    leaveService.getDayInfo(state.day.date).then(res => {
      if (!res.success || !res.data) {
        setPrepError('Could not load your schedule for this day, so the leave hours cannot be worked out. Try again shortly.');
        return;
      }
      setDayInfo(res.data);
      // Zero-hour segments are the unpaid break, not a shortfall.
      const usable = res.data.uncovered_segments.filter(s => s.hours > 0);
      setSegments(usable);
      if (usable.length === 0) {
        setPrepError(
          res.data.is_working_day
            ? 'Every scheduled hour on this day is already covered by a timesheet entry, so there is nothing to take as leave.'
            : 'You have no work schedule for this day, so partial-day leave cannot be calculated. Ask your manager to set your work schedule.'
        );
      }
    }).catch(() => {
      setPrepError('Could not load your schedule for this day, so the leave hours cannot be worked out. Try again shortly.');
    }).finally(() => setPrepping(false));
  }, [state]);

  const toggleSegment = (i: number) => {
    setSkipped(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const chosen = segments.filter((_, i) => !skipped.has(i));
  const chosenHours = chosen.reduce((n, s) => n + s.hours, 0);

  // Every request this submit will send. Partial days and multi-run ranges
  // both become several requests — non-overlapping windows on one date, and
  // one whole-day range per contiguous run of untouched days.
  const payloads = useMemo<CreateLeaveRequest[]>(() => {
    const base = { leave_type: leaveType, reason: reason.trim() || undefined };
    if (state.kind === 'range') {
      return state.runs.map(r => ({ ...base, start_date: r.start, end_date: r.end }));
    }
    if (isPartialDay) {
      return segments
        .filter((_, i) => !skipped.has(i))
        .map(seg => ({
          ...base,
          start_date: state.day.date,
          end_date: state.day.date,
          start_time: seg.start_time,
          end_time: seg.end_time,
        }));
    }
    return [{ ...base, start_date: state.day.date, end_date: state.day.date }];
  }, [state, leaveType, reason, isPartialDay, segments, skipped]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    let submitted = 0;
    const notes: string[] = [];
    const partialWarning = () => (submitted > 0
      ? ` ${submitted} of ${payloads.length} requests were already submitted — close this and check the list below.`
      : '');
    try {
      for (const payload of payloads) {
        const res = await leaveService.createRequest(payload);
        if (!res.success) {
          setError(`${res.error.message}${partialWarning()}`);
          return;
        }
        submitted += 1;
        // The server trims a window around hours already worked; say so.
        const warning = (res as { warning?: string }).warning;
        if (warning) notes.push(warning);
      }
      const what = payloads.length === 1 ? 'Leave request' : `${payloads.length} leave requests`;
      onSubmitted([`${what} submitted for approval.`, ...notes].join(' '));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      // Prefer the server's message (overlap details, balance shortfalls) over
      // an axios-wrapped HTTP status.
      const serverMsg = e?.response?.data?.error?.message;
      setError(`${serverMsg || e?.message || 'Failed to submit leave request'}${partialWarning()}`);
    } finally {
      setSubmitting(false);
    }
  };

  const available = availableFor(leaveType);
  const rangeLabel = state.kind === 'range'
    ? state.runs.map(r => (r.start === r.end
        ? formatLocalDateInTz(r.start, orgTz)
        : `${formatLocalDateInTz(r.start, orgTz)} — ${formatLocalDateInTz(r.end, orgTz)}`)).join(', ')
    : formatLocalDateInTz(state.day.date, orgTz);

  // Never fall back to a whole-day request on a day that was partly worked: it
  // would collide with the existing timesheet entry and become unapprovable,
  // with nothing on screen to explain why.
  const canSubmit = !submitting && !prepping && !prepError && payloads.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Request leave</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-neutral-600">
            <p><span className="font-medium text-neutral-800">{rangeLabel}</span></p>
            {state.kind === 'day' && !isPartialDay && (
              <p className="mt-1">Full scheduled day.</p>
            )}
            {state.kind === 'range' && (
              <p className="mt-1">
                Whole-day leave for each scheduled working day
                {state.runs.length > 1 ? ' in these ranges' : ' in this range'} (public holidays are skipped).
              </p>
            )}
          </div>

          {isPartialDay && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-2">
              {prepping ? (
                <p className="text-sm text-neutral-500">Working out the hours you missed…</p>
              ) : prepError ? (
                <p className="text-sm text-red-600">{prepError}</p>
              ) : (
                <>
                  {dayInfo && (
                    <p className="text-xs text-neutral-500">
                      Scheduled <strong>{dayInfo.scheduled_start_time}–{dayInfo.scheduled_end_time}</strong> ({hrs(dayInfo.scheduled_hours)}h)
                      {' · '}worked <strong>{hrs(dayInfo.worked_hours)}h</strong>
                      {dayInfo.worked_segments.length > 0 && (
                        <> ({dayInfo.worked_segments.map(w => `${w.start_time}–${w.end_time}`).join(', ')})</>
                      )}
                    </p>
                  )}
                  <p className="text-sm font-medium text-neutral-800">
                    {segments.length === 1
                      ? 'Hours to take as leave:'
                      : `You were away in ${segments.length} blocks on this day:`}
                  </p>
                  {segments.map((seg, i) => (
                    <label key={`${seg.start_time}-${seg.end_time}`} className="flex items-center gap-2 text-sm text-neutral-700">
                      <input
                        type="checkbox"
                        checked={!skipped.has(i)}
                        onChange={() => toggleSegment(i)}
                        className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        <strong>{seg.start_time}–{seg.end_time}</strong>
                        <span className="text-neutral-500"> · {hrs(seg.hours)}h</span>
                      </span>
                    </label>
                  ))}
                  {segments.length > 1 && (
                    <p className="text-xs text-neutral-500">
                      Submitted as {chosen.length} separate request{chosen.length !== 1 ? 's' : ''} so the leave fits around the hours you worked.
                    </p>
                  )}
                  <p className="text-sm text-neutral-800">
                    Total: <strong>{hrs(chosenHours)}h</strong>
                    {dayInfo && chosen.length === segments.length
                      && Math.abs(chosenHours - dayInfo.remaining_scheduled_hours) < 0.01 && (
                      <span className="text-neutral-500"> — covers the day's shortfall exactly.</span>
                    )}
                  </p>
                  {chosen.length === 0 && (
                    <p className="text-sm text-red-600">Select at least one block to request.</p>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Leave type</label>
            <select
              value={leaveType}
              onChange={e => setLeaveType(e.target.value as LeaveType)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {LEAVE_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {available != null && (
              <p className="text-xs text-neutral-500 mt-1">Available balance: {hrs(available)}h</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="e.g. Left early — unwell"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
            >
              Cancel
            </button>
            <Button onClick={submit} disabled={!canSubmit}>
              {submitting ? 'Submitting…' : prepping ? 'Loading…' : 'Submit request'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyTimesheet;
